// Dashboard.jsx - Updated without FiDollarSign icons
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { auth } from './config/firebase';
import { useAuth } from './AuthContext';
import './Dashboard.css';
import { 
  FiCalendar, 
  FiBriefcase, 
  FiUsers, 
  FiTrendingUp, 
  FiLogOut,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiGrid,
  FiMapPin,
  FiPhone,
  FiMail,
  FiGlobe,
  FiEdit,
  FiPlus,
  FiXCircle,
  FiCreditCard,
  FiActivity,
  FiBarChart2,
  FiPieChart
} from 'react-icons/fi';

export default function Dashboard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [financialStats, setFinancialStats] = useState({
    totalRevenue: 0,
    todayRevenue: 0,
    weekRevenue: 0,
    monthRevenue: 0,
    pendingRevenue: 0,
    avgBookingValue: 0,
    totalPaidBookings: 0,
    paymentMethods: {},
    revenueByService: {},
    revenueByDay: {},
    topServices: [],
    peakDays: []
  });
  const [timeFilter, setTimeFilter] = useState('month');

  const fetchUserDataAndBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      const db = getFirestore();

      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        setUserData(userDocSnap.data());
      } else {
        setUserData({
          email: currentUser.email,
          displayName: currentUser.displayName || 'User',
          createdAt: currentUser.metadata.creationTime
        });
      }

      const businessesQuery = query(
        collection(db, 'businesses'),
        where('ownerId', '==', currentUser.uid)
      );
      
      const businessesSnapshot = await getDocs(businessesQuery);
      const businessesList = businessesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(b => !b.isDeleted);

      setBusinesses(businessesList);
      
      if (businessesList.length > 0) {
        setSelectedBusiness(businessesList[0]);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchUserDataAndBusinesses();
    }
  }, [currentUser, fetchUserDataAndBusinesses]);

  const fetchAppointmentsAndCalculateStats = useCallback(async () => {
    try {
      const db = getFirestore();
      const businessIds = businesses.map(business => business.id);
      
      if (businessIds.length === 0) {
        setAppointments([]);
        setAllAppointments([]);
        return;
      }
      
      const appointmentsRef = collection(db, 'appointments');
      const allAppointmentsQuery = query(
        appointmentsRef, 
        where('businessId', 'in', businessIds),
        orderBy('createdAt', 'desc')
      );
      
      const allSnapshot = await getDocs(allAppointmentsQuery);
      const allAppointmentsList = [];
      
      allSnapshot.forEach((doc) => {
        allAppointmentsList.push({ id: doc.id, ...doc.data() });
      });
      
      setAllAppointments(allAppointmentsList);
      
      const stats = calculateFinancialStats(allAppointmentsList);
      console.log('Calculated Stats:', stats);
      setFinancialStats(stats);
      
      const now = new Date().toISOString().split('T')[0];
      const upcomingQuery = query(
        appointmentsRef, 
        where('businessId', 'in', businessIds),
        where('date', '>=', now)
      );
      
      const upcomingSnapshot = await getDocs(upcomingQuery);
      const upcomingList = [];
      
      upcomingSnapshot.forEach((doc) => {
        const appointment = { id: doc.id, ...doc.data() };
        if (appointment.status !== 'cancelled') {
          upcomingList.push(appointment);
        }
      });
      
      upcomingList.sort((a, b) => new Date(a.date) - new Date(b.date));
      setAppointments(upcomingList);
      
      const completedTransactions = allAppointmentsList
        .filter(a => a.status === 'confirmed' && a.payment?.status === 'successful')
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        })
        .slice(0, 10);
      
      setTransactions(completedTransactions);
      
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  }, [businesses]);

  useEffect(() => {
    if (businesses.length > 0) {
      fetchAppointmentsAndCalculateStats();
    }
  }, [businesses, fetchAppointmentsAndCalculateStats]);

  const calculateFinancialStats = (appointments) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    let totalRevenue = 0;
    let todayRevenue = 0;
    let weekRevenue = 0;
    let monthRevenue = 0;
    let pendingRevenue = 0;
    let totalPaidBookings = 0;
    
    const serviceStats = {};
    const dayStats = {};
    const paymentMethods = {};

    appointments.forEach((appointment) => {
      // Use payment.paidAt if available, otherwise use createdAt
      const paidAtDate = appointment.payment?.paidAt?.toDate ? 
        appointment.payment.paidAt.toDate() : 
        appointment.payment?.paidAt ? 
          new Date(appointment.payment.paidAt) :
          appointment.createdAt?.toDate ? 
            appointment.createdAt.toDate() : 
            new Date(appointment.createdAt);
      
      // Prioritize payment.amount, then service.price
      const amount = parseFloat(
        appointment.payment?.amount || 
        appointment.service?.price || 
        0
      );
      
      const serviceName = appointment.service?.name || 'Unknown Service';
      const paymentStatus = appointment.payment?.status || 'pending';
      
      // Revenue calculations for successful payments
      if (paymentStatus === 'successful') {
        totalRevenue += amount;
        totalPaidBookings++;

        if (paidAtDate >= today) {
          todayRevenue += amount;
        }
        if (paidAtDate >= weekAgo) {
          weekRevenue += amount;
        }
        if (paidAtDate >= monthStart) {
          monthRevenue += amount;
        }

        // Track payment methods (if available)
        const method = appointment.payment?.method || 'Paystack';
        paymentMethods[method] = (paymentMethods[method] || 0) + amount;

        // Service revenue tracking
        if (!serviceStats[serviceName]) {
          serviceStats[serviceName] = { revenue: 0, bookings: 0 };
        }
        serviceStats[serviceName].revenue += amount;
        serviceStats[serviceName].bookings++;

        // Day of week tracking (use appointment date, not payment date)
        const dayName = new Date(appointment.date).toLocaleDateString('en-US', { weekday: 'long' });
        dayStats[dayName] = (dayStats[dayName] || 0) + 1;
      }

      // Pending revenue (confirmed bookings without successful payment)
      if ((appointment.status === 'pending' || appointment.status === 'confirmed') && 
          paymentStatus !== 'successful') {
        pendingRevenue += amount;
      }
    });

    // Calculate average booking value
    const avgBookingValue = totalPaidBookings > 0 ? totalRevenue / totalPaidBookings : 0;

    // Get top services by revenue
    const topServices = Object.entries(serviceStats)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    // Get peak days
    const peakDays = Object.entries(dayStats)
      .map(([day, count]) => ({ day, count }))
      .sort((a, b) => b.count - a.count);

    return {
      totalRevenue,
      todayRevenue,
      weekRevenue,
      monthRevenue,
      pendingRevenue,
      avgBookingValue,
      totalPaidBookings,
      paymentMethods,
      topServices,
      peakDays
    };
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const calculateBookingStats = () => {
    const confirmed = allAppointments.filter(a => a.status === 'confirmed').length;
    const pending = allAppointments.filter(a => a.status === 'pending').length;
    const cancelled = allAppointments.filter(a => a.status === 'cancelled').length;
    const uniqueClients = new Set(allAppointments.map(a => a.userId)).size;

    return { confirmed, pending, cancelled, uniqueClients };
  };

  const bookingStats = calculateBookingStats();

  const stats = [
    { 
      icon: <FiTrendingUp />, 
      label: 'Total Revenue', 
      value: `R ${financialStats.totalRevenue.toFixed(2)}`, 
      change: financialStats.monthRevenue > 0 ? `+R${financialStats.monthRevenue.toFixed(2)} this month` : 'R0 this month',
      color: '#10b981' 
    },
    { 
      icon: <FiCalendar />, 
      label: 'Total Bookings', 
      value: allAppointments.length, 
      change: `${bookingStats.confirmed} confirmed`,
      color: '#6366f1' 
    },
    { 
      icon: <FiCreditCard />, 
      label: 'Avg Booking Value', 
      value: `R ${financialStats.avgBookingValue.toFixed(2)}`, 
      change: `${financialStats.totalPaidBookings} paid bookings`,
      color: '#f59e0b' 
    },
    { 
      icon: <FiUsers />, 
      label: 'Total Clients', 
      value: bookingStats.uniqueClients, 
      change: `${bookingStats.pending} pending`,
      color: '#ec4899' 
    }
  ];

  const upcomingBookings = appointments.slice(0, 5).map(appointment => ({
    id: appointment.id,
    client: appointment.userName || 'Unknown',
    clientEmail: appointment.userEmail || '',
    clientPhoto: appointment.userPhotoURL || null,
    service: appointment.service?.name || 'Service',
    serviceDescription: appointment.service?.description || '',
    businessName: appointment.businessName || 'Business',
    date: appointment.date,
    time: appointment.timeSlot?.display || appointment.timeSlot || 'N/A',
    status: appointment.status || 'pending',
    amount: `R${appointment.service?.price || 0}`,
    notes: appointment.notes || '',
    paymentStatus: appointment.payment?.status || 'pending',
    paymentReference: appointment.payment?.reference || ''
  }));

  const recentTransactions = transactions.slice(0, 5).map(transaction => ({
    id: transaction.id,
    client: transaction.userName || 'Unknown',
    amount: `R${transaction.payment?.amount || transaction.service?.price || 0}`,
    service: transaction.service?.name || 'Service',
    date: transaction.date,
    paidAt: transaction.payment?.paidAt,
    status: transaction.payment?.status || 'pending',
    reference: transaction.payment?.reference || 'N/A',
    transactionId: transaction.payment?.transactionId || '',
    method: transaction.payment?.method || 'Paystack'
  }));

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-top-header">
        <div className="header-left">
          <div className="logo-icon">
            <FiGrid />
          </div>
          <div>
            <h1 className="dashboard-title">My Dashboard</h1>
            <p className="dashboard-subtitle">Welcome back, {userData?.displayName || currentUser?.email}!</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="logout-button" onClick={handleLogout}>
            <FiLogOut />
            <span>Logout</span>
          </button>
          <button className="create-business-button" onClick={() => navigate('/create-business')}>
            <FiPlus />
            Create Business Profile
          </button>
          <button className="icon-button-labeled" onClick={() => navigate('/manage-businesses')}>
            <FiBriefcase />
            Manage Businesses
          </button>
        </div>
      </div>

      <div className="dashboard-container">
        <div className="info-cards-grid">
          <div className="info-card user-card">
            <div className="info-card-header">
              <h3>
                <FiUsers />
                User Profile
              </h3>
              <button className="edit-button">
                <FiEdit />
              </button>
            </div>
            <div className="info-card-content">
              <div className="user-avatar">
                {currentUser?.photoURL ? (
                  <img src={currentUser.photoURL} alt="Profile" />
                ) : (
                  <div className="avatar-placeholder">
                    {(userData?.displayName || currentUser?.email || 'U')[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div className="user-details">
                <h4>{userData?.displayName || 'Set your name'}</h4>
                <div className="user-detail-item">
                  <FiMail />
                  <span>{currentUser?.email}</span>
                </div>
                {userData?.phone && (
                  <div className="user-detail-item">
                    <FiPhone />
                    <span>{userData.phone}</span>
                  </div>
                )}
                <div className="user-meta">
                  <span className="user-badge">Account Owner</span>
                  <span className="user-since">
                    Member since {new Date(userData?.createdAt || currentUser?.metadata.creationTime).toLocaleDateString()}
                  </span>
                </div>
                <button className="view-all-transactions" onClick={() => navigate('/edit-banking')} style={{ marginTop: 12 }}>
                  Update Payout Details
                </button>
              </div>
            </div>
          </div>

          <div className="info-card business-card">
            <div className="info-card-header">
              <h3><FiBriefcase /> Business Profile</h3>
              {selectedBusiness && (
                <button className="edit-button" onClick={() => navigate(`/edit-business/${selectedBusiness.id}`)}>
                  <FiEdit />
                </button>
              )}
              {businesses.length > 1 && (
                <select 
                  className="business-selector"
                  value={selectedBusiness?.id || ''}
                  onChange={(e) => {
                    const business = businesses.find(b => b.id === e.target.value);
                    setSelectedBusiness(business);
                  }}
                >
                  {businesses.map(business => (
                    <option key={business.id} value={business.id}>
                      {business.businessName}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="info-card-content">
              {selectedBusiness ? (
                <>
                  {selectedBusiness.profileImageUrl && (
                    <div className="business-image">
                      <img src={selectedBusiness.profileImageUrl} alt={selectedBusiness.businessName} />
                    </div>
                  )}
                  <div className="business-details">
                    <h4>{selectedBusiness.businessName}</h4>
                    <span className="business-category">{selectedBusiness.category}</span>
                    <p className="business-description">{selectedBusiness.description}</p>
                    
                    <div className="business-info-list">
                      <div className="business-detail-item">
                        <FiMapPin />
                        <span>{selectedBusiness.address}</span>
                      </div>
                      <div className="business-detail-item">
                        <FiPhone />
                        <span>{selectedBusiness.phone}</span>
                      </div>
                      {selectedBusiness.email && (
                        <div className="business-detail-item">
                          <FiMail />
                          <span>{selectedBusiness.email}</span>
                        </div>
                      )}
                      {selectedBusiness.website && (
                        <div className="business-detail-item">
                          <FiGlobe />
                          <a href={selectedBusiness.website} target="_blank" rel="noopener noreferrer">
                            {selectedBusiness.website}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="business-status">
                      <span className={`status-badge ${selectedBusiness.status}`}>
                        {selectedBusiness.status === 'active' ? <FiCheckCircle /> : <FiAlertCircle />}
                        {selectedBusiness.status}
                      </span>
                      {selectedBusiness.isVerified ? (
                        <span className="verified-badge">
                          <FiCheckCircle />
                          Verified
                        </span>
                      ) : (
                        <span className="pending-badge">
                          <FiClock />
                          Pending Verification
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-business">
                  <FiBriefcase size={48} />
                  <p>No business profile yet</p>
                  <button className="create-business-button"
                   onClick={() => navigate('/create-business')}
                  >
                    <FiPlus />
                    Create Business Profile
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-tabs">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FiGrid /> Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            <FiCalendar /> Bookings
          </button>
          <button 
            className={`tab-button ${activeTab === 'finance' ? 'active' : ''}`}
            onClick={() => setActiveTab('finance')}
          >
            <FiCreditCard /> Finance
          </button>
          <button 
            className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FiBarChart2 /> Analytics
          </button>
        </div>

        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-content">
                <p className="stat-label">{stat.label}</p>
                <h3 className="stat-value">{stat.value}</h3>
                <span className="stat-change">{stat.change}</span>
              </div>
            </div>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="dashboard-content">
            <div className="content-left">
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">
                    <FiCalendar />
                    Upcoming Bookings
                  </h2>
                  <button className="view-all-button" onClick={() => setActiveTab('bookings')}>
                    View All
                  </button>
                </div>
                <div className="bookings-list">
                  {upcomingBookings.length > 0 ? (
                    upcomingBookings.map(booking => (
                      <div key={booking.id} className="booking-item">
                        <div className="booking-main">
                          <div className="booking-client-info">
                            {booking.clientPhoto ? (
                              <img src={booking.clientPhoto} alt={booking.client} className="client-avatar" />
                            ) : (
                              <div className="client-avatar-placeholder">
                                <FiUsers />
                              </div>
                            )}
                            <div className="client-details">
                              <h4>{booking.client}</h4>
                              <p className="client-email">
                                <FiMail />
                                {booking.clientEmail}
                              </p>
                            </div>
                          </div>
                          <span className={`booking-status ${booking.status}`}>
                            {booking.status === 'confirmed' ? <FiCheckCircle /> : <FiAlertCircle />}
                            {booking.status}
                          </span>
                        </div>
                        
                        <div className="booking-details-section">
                          <div className="booking-detail-row">
                            <div className="booking-info-item">
                              <FiBriefcase />
                              <div>
                                <span className="label">Service</span>
                                <span className="value">{booking.service}</span>
                              </div>
                            </div>
                            <div className="booking-info-item">
                              <FiTrendingUp />
                              <div>
                                <span className="label">Amount</span>
                                <span className="value amount-value">{booking.amount}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="booking-detail-row">
                            <div className="booking-info-item">
                              <FiCalendar />
                              <div>
                                <span className="label">Date</span>
                                <span className="value">{booking.date}</span>
                              </div>
                            </div>
                            <div className="booking-info-item">
                              <FiClock />
                              <div>
                                <span className="label">Time</span>
                                <span className="value">{booking.time}</span>
                              </div>
                            </div>
                          </div>

                          {booking.businessName && (
                            <div className="booking-business-tag">
                              <FiBriefcase />
                              {booking.businessName}
                            </div>
                          )}

                          {booking.notes && (
                            <div className="booking-notes">
                              <FiEdit />
                              <span>{booking.notes}</span>
                            </div>
                          )}

                          {booking.paymentReference && (
                            <div className="booking-payment-ref">
                              <FiCreditCard />
                              <span>Ref: {booking.paymentReference}</span>
                              <span className={`payment-badge ${booking.paymentStatus}`}>
                                {booking.paymentStatus}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data-message">No upcoming bookings</p>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">
                    <FiTrendingUp />
                    Top Services by Revenue
                  </h2>
                </div>
                <div className="services-list">
                  {financialStats.topServices.length > 0 ? (
                    financialStats.topServices.map((service, index) => (
                      <div key={index} className="service-item">
                        <div className="service-info">
                          <span className="service-name">{service.name}</span>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ 
                                width: `${(service.revenue / financialStats.topServices[0].revenue) * 100}%` 
                              }}
                            ></div>
                          </div>
                          <span className="service-revenue">R{service.revenue.toFixed(2)}</span>
                        </div>
                        <span className="service-count">{service.bookings} bookings</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data-message">No service data available</p>
                  )}
                </div>
              </div>
            </div>

            <div className="content-right">
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">
                    <FiCreditCard />
                    Recent Transactions
                  </h2>
                </div>
                <div className="transactions-list">
                  {recentTransactions.length > 0 ? (
                    recentTransactions.map(transaction => (
                      <div key={transaction.id} className="transaction-item">
                        <div className="transaction-info">
                          <h4>{transaction.client}</h4>
                          <p className="transaction-service">{transaction.service}</p>
                          <span className="transaction-date">{transaction.date}</span>
                          <span className="transaction-reference">Ref: {transaction.reference}</span>
                        </div>
                        <div className="transaction-amount">
                          {transaction.amount}
                          <span className={`transaction-status ${transaction.status}`}>
                            {transaction.status}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data-message">No recent transactions</p>
                  )}
                </div>
                <button className="view-all-transactions" onClick={() => setActiveTab('finance')}>
                  View All Transactions
                </button>
              </div>

              <div className="card quick-stats-card">
                <h2 className="card-title">
                  <FiActivity />
                  Quick Stats
                </h2>
                <div className="quick-stats">
                  <div className="quick-stat-item">
                    <span className="quick-stat-label">Today's Revenue</span>
                    <span className="quick-stat-value">R{financialStats.todayRevenue.toFixed(2)}</span>
                  </div>
                  <div className="quick-stat-item">
                    <span className="quick-stat-label">This Week</span>
                    <span className="quick-stat-value">R{financialStats.weekRevenue.toFixed(2)}</span>
                  </div>
                  <div className="quick-stat-item">
                    <span className="quick-stat-label">This Month</span>
                    <span className="quick-stat-value">R{financialStats.monthRevenue.toFixed(2)}</span>
                  </div>
                  <div className="quick-stat-item">
                    <span className="quick-stat-label">Pending Revenue</span>
                    <span className="quick-stat-value pending">R{financialStats.pendingRevenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'finance' && (
          <div className="finance-content">
            <div className="finance-header">
              <h2 className="section-title">
                <FiTrendingUp />
                Financial Overview
              </h2>
              <div className="time-filter">
                <button 
                  className={timeFilter === 'today' ? 'active' : ''}
                  onClick={() => setTimeFilter('today')}
                >
                  Today
                </button>
                <button 
                  className={timeFilter === 'week' ? 'active' : ''}
                  onClick={() => setTimeFilter('week')}
                >
                  Week
                </button>
                <button 
                  className={timeFilter === 'month' ? 'active' : ''}
                  onClick={() => setTimeFilter('month')}
                >
                  Month
                </button>
                <button 
                  className={timeFilter === 'all' ? 'active' : ''}
                  onClick={() => setTimeFilter('all')}
                >
                  All Time
                </button>
              </div>
            </div>

            <div className="finance-grid">
              <div className="card finance-summary">
                <h3 className="card-title">Revenue Summary</h3>
                <div className="revenue-breakdown">
                  <div className="revenue-item">
                    <span className="revenue-label">Total Revenue</span>
                    <span className="revenue-value large">R{financialStats.totalRevenue.toFixed(2)}</span>
                  </div>
                  <div className="revenue-item">
                    <span className="revenue-label">Today</span>
                    <span className="revenue-value">R{financialStats.todayRevenue.toFixed(2)}</span>
                  </div>
                  <div className="revenue-item">
                    <span className="revenue-label">This Week</span>
                    <span className="revenue-value">R{financialStats.weekRevenue.toFixed(2)}</span>
                  </div>
                  <div className="revenue-item">
                    <span className="revenue-label">This Month</span>
                    <span className="revenue-value">R{financialStats.monthRevenue.toFixed(2)}</span>
                  </div>
                  <div className="revenue-item pending-item">
                    <span className="revenue-label">Pending</span>
                    <span className="revenue-value">R{financialStats.pendingRevenue.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="card booking-statistics">
                <h3 className="card-title">Booking Statistics</h3>
                <div className="booking-stats-grid">
                  <div className="stat-box confirmed">
                    <FiCheckCircle />
                    <span className="stat-number">{bookingStats.confirmed}</span>
                    <span className="stat-label">Confirmed</span>
                  </div>
                  <div className="stat-box pending">
                    <FiAlertCircle />
                    <span className="stat-number">{bookingStats.pending}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                  <div className="stat-box cancelled">
                    <FiXCircle />
                    <span className="stat-number">{bookingStats.cancelled}</span>
                    <span className="stat-label">Cancelled</span>
                  </div>
                  <div className="stat-box total">
                    <FiCalendar />
                    <span className="stat-number">{allAppointments.length}</span>
                    <span className="stat-label">Total</span>
                  </div>
                </div>
              </div>

              <div className="card all-transactions">
                <h3 className="card-title">All Transactions</h3>
                <div className="transactions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Service</th>
                        <th>Amount</th>
                        <th>Reference</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(transaction => (
                        <tr key={transaction.id}>
                          <td>{transaction.date}</td>
                          <td>{transaction.userName || 'Unknown'}</td>
                          <td>{transaction.service?.name || 'Service'}</td>
                          <td className="amount">R{transaction.payment?.amount || transaction.service?.price || 0}</td>
                          <td className="reference">
                            <div className="reference-info">
                              <span>{transaction.payment?.reference || 'N/A'}</span>
                              {transaction.payment?.transactionId && (
                                <span className="transaction-id">ID: {transaction.payment.transactionId}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="status-column">
                              <span className={`status-badge ${transaction.payment?.status || 'pending'}`}>
                                {transaction.payment?.status || 'pending'}
                              </span>
                              {transaction.payment?.method && (
                                <span className="payment-method-tag">{transaction.payment.method}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {transactions.length === 0 && (
                    <p className="no-data-message">No transactions yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="bookings-content">
            <div className="bookings-header">
              <h2 className="section-title">
                <FiCalendar />
                All Bookings
              </h2>
              <div className="bookings-filter">
                <button className="filter-btn active">All</button>
                <button className="filter-btn">Upcoming</button>
                <button className="filter-btn">Past</button>
                <button className="filter-btn">Cancelled</button>
              </div>
            </div>

            <div className="bookings-grid">
              {allAppointments.map(appointment => (
                <div key={appointment.id} className="booking-card">
                  <div className="booking-card-header">
                    <h4>{appointment.userName || 'Unknown Client'}</h4>
                    <span className={`status-badge ${appointment.status}`}>
                      {appointment.status === 'confirmed' && <FiCheckCircle />}
                      {appointment.status === 'pending' && <FiAlertCircle />}
                      {appointment.status === 'cancelled' && <FiXCircle />}
                      {appointment.status}
                    </span>
                  </div>
                  <div className="booking-card-body">
                    <div className="booking-detail">
                      <FiBriefcase />
                      <span>{appointment.service?.name || 'Service'}</span>
                    </div>
                    <div className="booking-detail">
                      <FiCalendar />
                      <span>{appointment.date}</span>
                    </div>
                    <div className="booking-detail">
                      <FiClock />
                      <span>{appointment.timeSlot?.display || 'Time not set'}</span>
                    </div>
                    <div className="booking-detail">
                      <FiTrendingUp />
                      <span>R{appointment.service?.price || 0}</span>
                    </div>
                    {appointment.payment && (
                      <div className="booking-payment-info">
                        <FiCreditCard />
                        <span className={`payment-status ${appointment.payment.status}`}>
                          {appointment.payment.status}
                        </span>
                        {appointment.payment.reference && (
                          <span className="payment-ref">Ref: {appointment.payment.reference}</span>
                        )}
                      </div>
                    )}
                    <div className="booking-detail">
                      <FiMail />
                      <span>{appointment.userEmail || 'No email'}</span>
                    </div>
                  </div>
                </div>
              ))}
              {allAppointments.length === 0 && (
                <div className="no-bookings">
                  <FiCalendar size={48} />
                  <p>No bookings yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-content">
            <h2 className="section-title">
              <FiBarChart2 />
              Business Analytics
            </h2>

            <div className="analytics-grid">
              <div className="card analytics-card">
                <h3 className="card-title">
                  <FiPieChart />
                  Peak Booking Days
                </h3>
                <div className="peak-days-list">
                  {financialStats.peakDays.length > 0 ? (
                    financialStats.peakDays.map((day, index) => (
                      <div key={index} className="peak-day-item">
                        <span className="day-name">{day.day}</span>
                        <div className="day-bar">
                          <div 
                            className="day-bar-fill" 
                            style={{ 
                              width: `${(day.count / financialStats.peakDays[0].count) * 100}%` 
                            }}
                          ></div>
                        </div>
                        <span className="day-count">{day.count} bookings</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data-message">No booking data yet</p>
                  )}
                </div>
              </div>

              <div className="card analytics-card">
                <h3 className="card-title">
                  <FiTrendingUp />
                  Service Performance
                </h3>
                <div className="service-performance-list">
                  {financialStats.topServices.map((service, index) => (
                    <div key={index} className="performance-item">
                      <div className="performance-header">
                        <span className="rank">#{index + 1}</span>
                        <span className="service-name">{service.name}</span>
                      </div>
                      <div className="performance-stats">
                        <div className="performance-stat">
                          <span className="stat-label">Revenue</span>
                          <span className="stat-value">R{service.revenue.toFixed(2)}</span>
                        </div>
                        <div className="performance-stat">
                          <span className="stat-label">Bookings</span>
                          <span className="stat-value">{service.bookings}</span>
                        </div>
                        <div className="performance-stat">
                          <span className="stat-label">Avg Value</span>
                          <span className="stat-value">
                            R{(service.revenue / service.bookings).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ 
                            width: `${(service.revenue / financialStats.totalRevenue) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card analytics-card">
                <h3 className="card-title">
                  <FiUsers />
                  Client Insights
                </h3>
                <div className="client-insights">
                  <div className="insight-item">
                    <span className="insight-label">Total Unique Clients</span>
                    <span className="insight-value">{bookingStats.uniqueClients}</span>
                  </div>
                  <div className="insight-item">
                    <span className="insight-label">Avg Bookings per Client</span>
                    <span className="insight-value">
                      {bookingStats.uniqueClients > 0 
                        ? (allAppointments.length / bookingStats.uniqueClients).toFixed(1)
                        : 0}
                    </span>
                  </div>
                  <div className="insight-item">
                    <span className="insight-label">Repeat Client Rate</span>
                    <span className="insight-value">
                      {bookingStats.uniqueClients > 0 
                        ? ((allAppointments.length / bookingStats.uniqueClients > 1 ? 1 : 0) * 100).toFixed(0)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="card analytics-card">
                <h3 className="card-title">
                  <FiTrendingUp />
                  Revenue Breakdown
                </h3>
                <div className="revenue-chart">
                  <div className="chart-item">
                    <div className="chart-bar">
                      <div 
                        className="chart-bar-fill today" 
                        style={{ 
                          height: `${financialStats.totalRevenue > 0 ? (financialStats.todayRevenue / financialStats.totalRevenue) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="chart-label">Today</span>
                    <span className="chart-value">R{financialStats.todayRevenue.toFixed(0)}</span>
                  </div>
                  <div className="chart-item">
                    <div className="chart-bar">
                      <div 
                        className="chart-bar-fill week" 
                        style={{ 
                          height: `${financialStats.totalRevenue > 0 ? (financialStats.weekRevenue / financialStats.totalRevenue) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="chart-label">Week</span>
                    <span className="chart-value">R{financialStats.weekRevenue.toFixed(0)}</span>
                  </div>
                  <div className="chart-item">
                    <div className="chart-bar">
                      <div 
                        className="chart-bar-fill month" 
                        style={{ 
                          height: `${financialStats.totalRevenue > 0 ? (financialStats.monthRevenue / financialStats.totalRevenue) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="chart-label">Month</span>
                    <span className="chart-value">R{financialStats.monthRevenue.toFixed(0)}</span>
                  </div>
                  <div className="chart-item">
                    <div className="chart-bar">
                      <div 
                        className="chart-bar-fill total" 
                        style={{ height: '100%' }}
                      ></div>
                    </div>
                    <span className="chart-label">Total</span>
                    <span className="chart-value">R{financialStats.totalRevenue.toFixed(0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}